// src/SimpleProposalGenerator.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "./firebase";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "./styles.css";

// Constants
const FIXED_PAGES_COUNT = 6; // Project Details, Company Info, Costing, Compliance, Assumptions, Network Architecture

// Helper function to escape HTML to prevent XSS
const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// Rich Text Editor Configuration for Custom Sections
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    [{ 'font': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'align',
  'link', 'image'
];

// Default assumptions for contracts
const DEFAULT_ASSUMPTIONS = [
  {
    id: 1,
    text: "The scope of supply & services as understood and considered by bidder is fully defined in following sections: Scope of Work Details, Scope Matrix and Assumptions & Clarifications.",
    checked: true
  },
  {
    id: 2,
    text: "Bidder understands that the basic design is provided by engineering consultant of customer and which is part of the RFQ docs. Bidder shall continue the engineering on this basic design.",
    checked: true
  },
  {
    id: 3,
    text: "The solution is based on inputs received from customer. If there are additional inputs/updates received at a later stage, it will call for a design revaluation and lead to time and cost impact.",
    checked: true
  },
  {
    id: 4,
    text: "At the beginning of the project, Customer has to provide updated/As-Built 2D CAD files to Bidder to start the work.",
    checked: true
  },
  {
    id: 5,
    text: "Training provided under this contract and Operator's interface is assumed to be in English and Arabic language only.",
    checked: true
  },
  {
    id: 6,
    text: "Documentation provided under this contract is assumed to be in English language only.",
    checked: true
  },
  {
    id: 7,
    text: "Customer will provide all IT Hardware and network infrastructure as per contractor inputs.",
    checked: true
  },
  {
    id: 8,
    text: "Customer will arrange for necessary read/write access (wherever applicable) to all the data points/parameters.",
    checked: true
  },
];

/**
 * SimpleProposalGenerator - A proposal generator with flexible pages
 * 
 * Pages:
 * 1. Project Details
 * 2. Company Info (AI Generated)
 * 3. Costing Sheet (Editable Table)
 * 4. Compliance Table (Editable Table)
 * 5. Assumptions and Clarifications (Checkboxes)
 * 6. Network Architecture (System Architecture from SLD tab)
 * + Custom Sections (Dynamic)
 * 
 * Export: .doc format
 */
export default function SimpleProposalGenerator({ tenderId }) {
  // Page navigation
  const [currentPage, setCurrentPage] = useState(1);
  
  // Page 1: Project Details
  const [projectDetails, setProjectDetails] = useState({
    projectName: "",
    clientName: "",
    rfqNumber: "",
    projectDescription: "",
    submissionDate: "",
    validityPeriod: "90 days",
    contactPerson: "",
    contactEmail: "",
  });
  
  // Page 2: Company Info
  const [companyName, setCompanyName] = useState("");
  const [companyInfo, setCompanyInfo] = useState("");
  const [isGeneratingCompanyInfo, setIsGeneratingCompanyInfo] = useState(false);
  
  // Page 3: Costing Sheet
  const [costingItems, setCostingItems] = useState([]);
  
  // Page 4: Compliance Table
  const [complianceItems, setComplianceItems] = useState([]);
  
  // Page 5: Assumptions and Clarifications
  const [assumptions, setAssumptions] = useState(DEFAULT_ASSUMPTIONS);
  
  // Custom Sections (dynamic pages)
  const [customSections, setCustomSections] = useState([]);
  
  // System Architecture data (from Single Line Diagram)
  const [sldData, setSldData] = useState(null);
  
  // Editable Network Architecture content
  const [networkArchContent, setNetworkArchContent] = useState({
    architectureName: "",
    systemDescription: "",
    diagramImage: ""
  });
  
  // Loading & Status
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  
  // Helper function to load collection with optional orderBy (handles missing createdAt field)
  const loadCollection = async (collectionRef, orderField, orderDirection, transformFn) => {
    try {
      const orderedQuery = query(collectionRef, orderBy(orderField, orderDirection));
      const snapshot = await getDocs(orderedQuery);
      return snapshot.docs.map(transformFn);
    } catch (err) {
      // Fallback: load without orderBy if field doesn't exist
      const snapshot = await getDocs(collectionRef);
      return snapshot.docs.map(transformFn);
    }
  };

  // Load tender data
  useEffect(() => {
    if (!tenderId) return;
    
    const loadData = async () => {
      setLoading(true);
      try {
        // Load tender details
        const tenderRef = doc(db, "tenders", tenderId);
        const tenderSnap = await getDoc(tenderRef);
        if (tenderSnap.exists()) {
          const tenderData = tenderSnap.data();
          setProjectDetails(prev => ({
            ...prev,
            projectName: tenderData.title || "",
            clientName: tenderData.clientName || "",
            rfqNumber: tenderData.rfpNumber || "",
            projectDescription: tenderData.description || "",
          }));
        }
        
        // Load costing items
        const costingRef = collection(db, "tenders", tenderId, "costingItems");
        const costingData = await loadCollection(
          costingRef,
          "createdAt",
          "asc",
          (docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            quantity: docSnap.data().quantity || 1,
            unitPrice: docSnap.data().unitPrice || 0,
            totalPrice: docSnap.data().totalPrice || 0,
          })
        );
        setCostingItems(costingData);
        
        // Load compliance clauses
        const clausesRef = collection(db, "tenders", tenderId, "clauses");
        const clausesData = await loadCollection(
          clausesRef,
          "createdAt",
          "desc",
          (docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            status: docSnap.data().status || "Open",
            response: docSnap.data().response || "",
          })
        );
        setComplianceItems(clausesData);
        
        // Load System Architecture (SLD) data (initial load)
        try {
          const proposalSldRef = doc(db, "tenders", tenderId, "proposal", "sld_data");
          const sldSnap = await getDoc(proposalSldRef);
          if (sldSnap.exists()) {
            const data = sldSnap.data();
            setSldData(data);
            // Initialize editable content
            setNetworkArchContent({
              architectureName: data.architectureName || "",
              systemDescription: data.systemDescription || "",
              diagramImage: data.diagramImage || ""
            });
          }
        } catch (sldError) {
          console.error("Error loading SLD data:", sldError);
          // Non-critical error, continue
        }
        
      } catch (error) {
        console.error("Error loading data:", error);
        setStatusMessage("Error loading data");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [tenderId]);
  
  // Real-time listener for SLD data updates
  useEffect(() => {
    if (!tenderId) return;
    
    const proposalSldRef = doc(db, "tenders", tenderId, "proposal", "sld_data");
    const unsubscribe = onSnapshot(proposalSldRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log("📥 ====== SLD DATA RECEIVED IN PROPOSAL TAB ======");
        console.log("Architecture name:", data.architectureName || "❌ Missing");
        console.log("System description:", data.systemDescription ? `✅ Present (${data.systemDescription.length} chars)` : "❌ Missing");
        console.log("Diagram image:", data.diagramImage ? `✅ Present (${data.diagramImage.length} chars)` : "❌ MISSING");
        console.log("Export status:", data.exportStatus || "Unknown");
        console.log("PNG attempts:", data.pngExportAttempts || "N/A");
        console.log("Timestamp:", data.timestamp || "No timestamp");
        console.log("=================================================");
        setSldData(data);
        // Update editable content with latest data
        setNetworkArchContent({
          architectureName: data.architectureName || "",
          systemDescription: data.systemDescription || "",
          diagramImage: data.diagramImage || ""
        });
      }
    }, (error) => {
      console.error("Error listening to SLD data updates:", error);
    });
    
    return () => unsubscribe();
  }, [tenderId]);
  
  // Generate company info using AI
  const generateCompanyInfo = async () => {
    if (!companyName.trim()) {
      setStatusMessage("Please enter a company name");
      setTimeout(() => setStatusMessage(""), 3000);
      return;
    }
    
    setIsGeneratingCompanyInfo(true);
    setStatusMessage("Generating company information...");
    
    try {
      const response = await fetch('/api/generate-company-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyName: companyName.trim() }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate company info');
      }
      
      const data = await response.json();
      setCompanyInfo(data.companyInfo || "");
      setStatusMessage("Company information generated successfully!");
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (error) {
      console.error("Error generating company info:", error);
      setStatusMessage("Error generating company info. Please try again.");
      setTimeout(() => setStatusMessage(""), 3000);
    } finally {
      setIsGeneratingCompanyInfo(false);
    }
  };
  
  // Update costing item
  const updateCostingItem = (index, field, value) => {
    const updated = [...costingItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-calculate total price
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unitPrice) || 0;
      updated[index].totalPrice = qty * price;
    }
    
    setCostingItems(updated);
  };
  
  // Add new costing item
  const addCostingItem = () => {
    setCostingItems([...costingItems, {
      id: `new-${Date.now()}`,
      itemName: "",
      description: "",
      quantity: 1,
      unit: "Nos",
      unitPrice: 0,
      totalPrice: 0,
    }]);
  };
  
  // Remove costing item
  const removeCostingItem = (index) => {
    const updated = costingItems.filter((_, i) => i !== index);
    setCostingItems(updated);
  };
  
  // Update compliance item
  const updateComplianceItem = (index, field, value) => {
    const updated = [...complianceItems];
    updated[index] = { ...updated[index], [field]: value };
    setComplianceItems(updated);
  };
  
  // Add new compliance item
  const addComplianceItem = () => {
    setComplianceItems([...complianceItems, {
      id: `new-${Date.now()}`,
      number: "",
      text: "",
      status: "Open",
      response: "",
    }]);
  };
  
  // Remove compliance item
  const removeComplianceItem = (index) => {
    const updated = complianceItems.filter((_, i) => i !== index);
    setComplianceItems(updated);
  };
  
  // Calculate grand total for costing
  const grandTotal = costingItems.reduce((sum, item) => sum + (parseFloat(item.totalPrice) || 0), 0);
  
  // Toggle assumption checkbox
  const toggleAssumption = (id) => {
    const updated = assumptions.map(a => 
      a.id === id ? { ...a, checked: !a.checked } : a
    );
    setAssumptions(updated);
  };
  
  // Add new assumption
  const addAssumption = () => {
    const newId = assumptions.length > 0 ? Math.max(...assumptions.map(a => a.id)) + 1 : 1;
    setAssumptions([...assumptions, {
      id: newId,
      text: "",
      checked: true
    }]);
  };
  
  // Update assumption text
  const updateAssumption = (id, text) => {
    const updated = assumptions.map(a => 
      a.id === id ? { ...a, text } : a
    );
    setAssumptions(updated);
  };
  
  // Delete assumption
  const deleteAssumption = (id) => {
    const updated = assumptions.filter(a => a.id !== id);
    setAssumptions(updated);
  };
  
  // Add custom section
  const addCustomSection = () => {
    const newId = Date.now();
    const newSection = {
      id: newId,
      title: "New Section",
      content: "",
      pageNumber: FIXED_PAGES_COUNT + 1 + customSections.length // Start after fixed pages
    };
    setCustomSections([...customSections, newSection]);
    // Navigate to the new section
    setCurrentPage(newSection.pageNumber);
  };
  
  // Update custom section
  const updateCustomSection = (id, field, value) => {
    const updated = customSections.map(s => 
      s.id === id ? { ...s, [field]: value } : s
    );
    setCustomSections(updated);
  };
  
  // Delete custom section
  const deleteCustomSection = (id) => {
    if (window.confirm("Are you sure you want to delete this section?")) {
      const updated = customSections.filter(s => s.id !== id);
      // Renumber remaining sections
      const renumbered = updated.map((s, idx) => ({
        ...s,
        pageNumber: FIXED_PAGES_COUNT + 1 + idx
      }));
      setCustomSections(renumbered);
      // Navigate back if on deleted page
      if (currentPage > FIXED_PAGES_COUNT + renumbered.length) {
        setCurrentPage(1);
      }
    }
  };
  
  // Calculate total pages
  const totalPages = FIXED_PAGES_COUNT + customSections.length;
  
  // Export to DOC format
  const exportToDoc = () => {
    setStatusMessage("Generating document...");
    
    // Debug log to verify diagram image is present
    console.log("📄 ====== EXPORTING TO DOC ======");
    console.log("Network Architecture Content:");
    console.log("  - Architecture Name:", networkArchContent.architectureName || "❌ Missing");
    console.log("  - System Description:", networkArchContent.systemDescription ? `✅ Present (${networkArchContent.systemDescription.length} chars)` : "❌ Missing");
    console.log("  - Diagram Image:", networkArchContent.diagramImage ? `✅ Present (${networkArchContent.diagramImage.length} chars)` : "❌ MISSING");
    console.log("================================");
    
    try {
      // Build HTML content for the document
      let htmlContent = `
        <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset="utf-8">
          <title>Proposal - ${projectDetails.projectName}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; }
            h1 { color: #2563eb; text-align: center; margin-bottom: 30px; }
            h2 { color: #1e40af; border-bottom: 2px solid #2563eb; padding-bottom: 10px; margin-top: 30px; }
            h3 { color: #374151; margin-top: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th { background-color: #2563eb; color: white; padding: 12px; text-align: left; border: 1px solid #2563eb; }
            td { padding: 10px; border: 1px solid #e5e7eb; }
            tr:nth-child(even) { background-color: #f9fafb; }
            .total-row { font-weight: bold; background-color: #e5e7eb !important; }
            .info-box { background-color: #f0f9ff; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #bae6fd; }
            .status-compliant { color: #059669; }
            .status-open { color: #d97706; }
            .status-deviation { color: #dc2626; }
            p { line-height: 1.6; }
            .page-break { page-break-before: always; }
          </style>
        </head>
        <body>
      `;
      
      // Page 1: Project Details
      htmlContent += `
        <h1>PROPOSAL DOCUMENT</h1>
        <h2>1. Project Details</h2>
        <table>
          <tr><th style="width: 30%;">Project Name</th><td>${projectDetails.projectName || 'N/A'}</td></tr>
          <tr><th>Client Name</th><td>${projectDetails.clientName || 'N/A'}</td></tr>
          <tr><th>RFQ Number</th><td>${projectDetails.rfqNumber || 'N/A'}</td></tr>
          <tr><th>Submission Date</th><td>${projectDetails.submissionDate || new Date().toLocaleDateString()}</td></tr>
          <tr><th>Validity Period</th><td>${projectDetails.validityPeriod || '90 days'}</td></tr>
          <tr><th>Contact Person</th><td>${projectDetails.contactPerson || 'N/A'}</td></tr>
          <tr><th>Contact Email</th><td>${projectDetails.contactEmail || 'N/A'}</td></tr>
        </table>
        ${projectDetails.projectDescription ? `
          <h3>Project Description</h3>
          <p>${projectDetails.projectDescription}</p>
        ` : ''}
      `;
      
      // Page 2: Company Information
      htmlContent += `
        <div class="page-break"></div>
        <h2>2. Company Information</h2>
        <h3>${companyName || 'Company Name'}</h3>
        <div class="info-box">
          <p>${companyInfo || 'Company information not provided.'}</p>
        </div>
      `;
      
      // Page 3: Costing Sheet
      htmlContent += `
        <div class="page-break"></div>
        <h2>3. Costing Sheet</h2>
        <table>
          <tr>
            <th>Sr No</th>
            <th>Item Name</th>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Unit Price</th>
            <th>Total Price</th>
          </tr>
      `;
      
      if (costingItems.length > 0) {
        costingItems.forEach((item, idx) => {
          htmlContent += `
            <tr>
              <td>${idx + 1}</td>
              <td>${item.itemName || ''}</td>
              <td>${item.description || ''}</td>
              <td>${item.quantity || 0}</td>
              <td>${item.unit || 'Nos'}</td>
              <td>${parseFloat(item.unitPrice || 0).toLocaleString()}</td>
              <td>${parseFloat(item.totalPrice || 0).toLocaleString()}</td>
            </tr>
          `;
        });
        htmlContent += `
          <tr class="total-row">
            <td colspan="6" style="text-align: right;"><strong>GRAND TOTAL</strong></td>
            <td><strong>${grandTotal.toLocaleString()}</strong></td>
          </tr>
        `;
      } else {
        htmlContent += `
          <tr>
            <td colspan="7" style="text-align: center; color: #6b7280;">No costing items added</td>
          </tr>
        `;
      }
      
      htmlContent += `</table>`;
      
      // Page 4: Compliance Table
      htmlContent += `
        <div class="page-break"></div>
        <h2>4. Compliance Table</h2>
        <table>
          <tr>
            <th>Sr No</th>
            <th>Clause No</th>
            <th>Requirement</th>
            <th>Status</th>
            <th>Response</th>
          </tr>
      `;
      
      if (complianceItems.length > 0) {
        complianceItems.forEach((item, idx) => {
          const statusClass = item.status === 'Compliant' ? 'status-compliant' : 
                              item.status === 'Deviation' ? 'status-deviation' : 'status-open';
          htmlContent += `
            <tr>
              <td>${idx + 1}</td>
              <td>${item.number || ''}</td>
              <td>${item.text || item.requirement || item.clause || ''}</td>
              <td class="${statusClass}">${item.status || 'Open'}</td>
              <td>${item.response || ''}</td>
            </tr>
          `;
        });
      } else {
        htmlContent += `
          <tr>
            <td colspan="5" style="text-align: center; color: #6b7280;">No compliance items added</td>
          </tr>
        `;
      }
      
      htmlContent += `</table>`;
      
      // Page 5: Assumptions and Clarifications
      let sectionNumber = 5;
      const checkedAssumptions = assumptions.filter(a => a.checked);
      
      if (checkedAssumptions.length > 0) {
        htmlContent += `
          <div class="page-break"></div>
          <h2>${sectionNumber}. Assumptions and Clarifications</h2>
          <ol style="line-height: 2;">
        `;
        checkedAssumptions.forEach((assumption) => {
          htmlContent += `<li>${escapeHtml(assumption.text)}</li>`;
        });
        htmlContent += `</ol>`;
        sectionNumber++; // Increment for next section
      }
      
      // Page 6: Network Architecture (if available)
      if (networkArchContent.architectureName || networkArchContent.systemDescription || networkArchContent.diagramImage) {
        console.log("📝 Adding Network Architecture section to DOC export");
        htmlContent += `
          <div class="page-break"></div>
          <h2>${sectionNumber}. Network Architecture</h2>
        `;
        
        // Add architecture name if available
        if (networkArchContent.architectureName) {
          console.log("  ✅ Adding architecture name");
          htmlContent += `
            <h3>${escapeHtml(networkArchContent.architectureName)}</h3>
          `;
        }
        
        // Add diagram image if available
        if (networkArchContent.diagramImage) {
          console.log("  ✅ Adding diagram image - length:", networkArchContent.diagramImage.length);
          console.log("  ✅ Image data preview:", networkArchContent.diagramImage.substring(0, 100));
          htmlContent += `
            <div style="text-align: center; margin: 20px 0;">
              <img src="${networkArchContent.diagramImage}" alt="Network Architecture Diagram" style="max-width: 100%; height: auto; border: 1px solid #e5e7eb; border-radius: 4px;" />
              <p style="font-size: 13px; color: #6b7280; font-style: italic; margin-top: 10px;">
                Figure: ${escapeHtml(networkArchContent.architectureName || 'Network Architecture Diagram')}
              </p>
            </div>
          `;
        } else {
          console.log("  ❌ Diagram image is missing - not adding to export");
        }
        
        // Add system description if available
        if (networkArchContent.systemDescription) {
          htmlContent += `
            <h3>System Description</h3>
            <div style="line-height: 1.8; white-space: pre-wrap;">
              ${escapeHtml(networkArchContent.systemDescription)}
            </div>
          `;
        }
        
        sectionNumber++; // Increment for next section
      }
      
      // Custom Sections (with rich text HTML content)
      if (customSections.length > 0) {
        customSections.forEach((section) => {
          htmlContent += `
            <div class="page-break"></div>
            <h2>${sectionNumber}. ${escapeHtml(section.title)}</h2>
            <div style="line-height: 1.8;">${section.content || ''}</div>
          `;
          sectionNumber++; // Increment for each custom section
        });
      }
      
      // Close document
      htmlContent += `
        </body>
        </html>
      `;
      
      // Create blob and download
      // Using application/msword MIME type with HTML content is the standard approach
      // for creating Word-compatible .doc files from HTML in browsers.
      // Microsoft Word opens HTML files with .doc extension correctly.
      const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Proposal_${projectDetails.projectName || 'Document'}_${new Date().toISOString().split('T')[0]}.doc`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setStatusMessage("Document exported successfully!");
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (error) {
      console.error("Error exporting document:", error);
      setStatusMessage("Error exporting document");
      setTimeout(() => setStatusMessage(""), 3000);
    }
  };
  
  // Navigation
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="detail-card">
          <p>Loading proposal data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="simple-proposal-container">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '15px 20px',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#2563eb' }}>📄 Proposal Generator</h2>
          {(networkArchContent.architectureName || networkArchContent.systemDescription || networkArchContent.diagramImage) && (
            <div style={{ 
              marginTop: '8px', 
              fontSize: '13px', 
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>✅</span>
              <span>Network Architecture will be included in export</span>
            </div>
          )}
        </div>
        <button
          onClick={exportToDoc}
          style={{
            background: '#059669',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          📥 Export to DOC
        </button>
      </div>
      
      {/* Status Message */}
      {statusMessage && (
        <div style={{
          padding: '10px 15px',
          background: statusMessage.includes('Error') ? '#fef2f2' : '#ecfdf5',
          color: statusMessage.includes('Error') ? '#dc2626' : '#059669',
          borderRadius: '6px',
          marginBottom: '15px',
          border: `1px solid ${statusMessage.includes('Error') ? '#fecaca' : '#a7f3d0'}`,
        }}>
          {statusMessage}
        </div>
      )}
      
      {/* Page Tabs/Navigation */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '10px',
        flexWrap: 'wrap',
      }}>
        {[
          { num: 1, title: 'Project Details', icon: '📋' },
          { num: 2, title: 'Company Info', icon: '🏢' },
          { num: 3, title: 'Costing Sheet', icon: '💰' },
          { num: 4, title: 'Compliance', icon: '✅' },
          { num: 5, title: 'Assumptions', icon: '📝' },
          { num: 6, title: 'Network Architecture', icon: '🔌' },
        ].map(page => (
          <button
            key={page.num}
            onClick={() => goToPage(page.num)}
            style={{
              padding: '12px 20px',
              background: currentPage === page.num ? '#2563eb' : 'white',
              color: currentPage === page.num ? 'white' : '#374151',
              border: `2px solid ${currentPage === page.num ? '#2563eb' : '#e5e7eb'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              minWidth: '150px',
              transition: 'all 0.2s ease',
            }}
          >
            {page.icon} {page.title}
          </button>
        ))}
        
        {/* Custom Sections Tabs */}
        {customSections.map(section => (
          <button
            key={section.id}
            onClick={() => goToPage(section.pageNumber)}
            style={{
              padding: '12px 20px',
              background: currentPage === section.pageNumber ? '#2563eb' : 'white',
              color: currentPage === section.pageNumber ? 'white' : '#374151',
              border: `2px solid ${currentPage === section.pageNumber ? '#2563eb' : '#e5e7eb'}`,
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              minWidth: '150px',
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
          >
            📄 {section.title}
            {currentPage === section.pageNumber && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  deleteCustomSection(section.id);
                }}
                style={{
                  position: 'absolute',
                  top: '-5px',
                  right: '-5px',
                  background: '#ef4444',
                  color: 'white',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                ×
              </span>
            )}
          </button>
        ))}
        
        {/* Add Custom Section Button */}
        <button
          onClick={addCustomSection}
          style={{
            padding: '12px 20px',
            background: '#10b981',
            color: 'white',
            border: '2px solid #10b981',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            minWidth: '150px',
            transition: 'all 0.2s ease',
          }}
        >
          ➕ Add Section
        </button>
      </div>
      
      {/* Page Content */}
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '25px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        minHeight: '400px',
      }}>
        
        {/* Page 1: Project Details */}
        {currentPage === 1 && (
          <div>
            <h3 style={{ marginTop: 0, color: '#1e40af', borderBottom: '2px solid #2563eb', paddingBottom: '10px' }}>
              📋 Project Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  Project Name
                </label>
                <input
                  type="text"
                  value={projectDetails.projectName}
                  onChange={(e) => setProjectDetails({ ...projectDetails, projectName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                  placeholder="Enter project name"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  Client Name
                </label>
                <input
                  type="text"
                  value={projectDetails.clientName}
                  onChange={(e) => setProjectDetails({ ...projectDetails, clientName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                  placeholder="Enter client name"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  RFQ Number
                </label>
                <input
                  type="text"
                  value={projectDetails.rfqNumber}
                  onChange={(e) => setProjectDetails({ ...projectDetails, rfqNumber: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                  placeholder="Enter RFQ number"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  Submission Date
                </label>
                <input
                  type="date"
                  value={projectDetails.submissionDate}
                  onChange={(e) => setProjectDetails({ ...projectDetails, submissionDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  Validity Period
                </label>
                <input
                  type="text"
                  value={projectDetails.validityPeriod}
                  onChange={(e) => setProjectDetails({ ...projectDetails, validityPeriod: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                  placeholder="e.g., 90 days"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  Contact Person
                </label>
                <input
                  type="text"
                  value={projectDetails.contactPerson}
                  onChange={(e) => setProjectDetails({ ...projectDetails, contactPerson: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                  placeholder="Enter contact person"
                />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  Contact Email
                </label>
                <input
                  type="email"
                  value={projectDetails.contactEmail}
                  onChange={(e) => setProjectDetails({ ...projectDetails, contactEmail: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                  placeholder="Enter contact email"
                />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                  Project Description
                </label>
                <textarea
                  value={projectDetails.projectDescription}
                  onChange={(e) => setProjectDetails({ ...projectDetails, projectDescription: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    minHeight: '100px',
                    resize: 'vertical',
                  }}
                  placeholder="Enter project description"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Page 2: Company Info */}
        {currentPage === 2 && (
          <div>
            <h3 style={{ marginTop: 0, color: '#1e40af', borderBottom: '2px solid #2563eb', paddingBottom: '10px' }}>
              🏢 Company Information
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                Company Name
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                  }}
                  placeholder="Enter company name to generate info"
                />
                <button
                  onClick={generateCompanyInfo}
                  disabled={isGeneratingCompanyInfo || !companyName.trim()}
                  style={{
                    background: isGeneratingCompanyInfo ? '#9ca3af' : '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: isGeneratingCompanyInfo ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isGeneratingCompanyInfo ? '🔄 Generating...' : '🤖 Generate with AI'}
                </button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#374151' }}>
                Company Information
              </label>
              <textarea
                value={companyInfo}
                onChange={(e) => setCompanyInfo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '15px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  minHeight: '300px',
                  resize: 'vertical',
                  lineHeight: '1.6',
                }}
                placeholder="Enter company information or click 'Generate with AI' to auto-generate based on company name..."
              />
            </div>
          </div>
        )}
        
        {/* Page 3: Costing Sheet */}
        {currentPage === 3 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#1e40af', borderBottom: '2px solid #2563eb', paddingBottom: '10px' }}>
                💰 Costing Sheet
              </h3>
              <button
                onClick={addCostingItem}
                style={{
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                + Add Item
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#2563eb' }}>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb' }}>Sr</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb' }}>Item Name</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb' }}>Description</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '80px' }}>Qty</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '80px' }}>Unit</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '100px' }}>HSN Code</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '120px' }}>Country of Origin</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '120px' }}>Project Country</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '120px' }}>Delivery Terms</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '100px' }}>Unit Price</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '100px' }}>Total</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'center', border: '1px solid #2563eb', width: '60px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {costingItems.length === 0 ? (
                    <tr>
                      <td colSpan="12" style={{ padding: '30px', textAlign: 'center', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                        No costing items. Click "Add Item" to add new items.
                      </td>
                    </tr>
                  ) : (
                    costingItems.map((item, idx) => (
                      <tr key={item.id} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{idx + 1}</td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.itemName || ''}
                            onChange={(e) => updateCostingItem(idx, 'itemName', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.description || ''}
                            onChange={(e) => updateCostingItem(idx, 'description', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => updateCostingItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.unit || 'Nos'}
                            onChange={(e) => updateCostingItem(idx, 'unit', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.hsnCode || ''}
                            onChange={(e) => updateCostingItem(idx, 'hsnCode', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                            placeholder="HSN"
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.countryOfOrigin || ''}
                            onChange={(e) => updateCostingItem(idx, 'countryOfOrigin', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                            placeholder="Origin"
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.projectCountry || ''}
                            onChange={(e) => updateCostingItem(idx, 'projectCountry', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                            placeholder="Destination"
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.deliveryTerms || ''}
                            onChange={(e) => updateCostingItem(idx, 'deliveryTerms', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                            placeholder="Incoterms"
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="number"
                            value={item.unitPrice || 0}
                            onChange={(e) => updateCostingItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb', fontWeight: '600' }}>
                          {parseFloat(item.totalPrice || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <button
                            onClick={() => removeCostingItem(idx)}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                  {costingItems.length > 0 && (
                    <tr style={{ background: '#e5e7eb', fontWeight: 'bold' }}>
                      <td colSpan="10" style={{ padding: '12px', border: '1px solid #e5e7eb', textAlign: 'right' }}>
                        GRAND TOTAL
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #e5e7eb' }}>
                        {grandTotal.toLocaleString()}
                      </td>
                      <td style={{ border: '1px solid #e5e7eb' }}></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Page 4: Compliance Table */}
        {currentPage === 4 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#1e40af', borderBottom: '2px solid #2563eb', paddingBottom: '10px' }}>
                ✅ Compliance Table
              </h3>
              <button
                onClick={addComplianceItem}
                style={{
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                + Add Item
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#2563eb' }}>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '50px' }}>Sr</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '100px' }}>Clause No</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb' }}>Requirement</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb', width: '120px' }}>Status</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'left', border: '1px solid #2563eb' }}>Response</th>
                    <th style={{ padding: '12px', color: 'white', textAlign: 'center', border: '1px solid #2563eb', width: '60px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {complianceItems.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                        No compliance items. Click "Add Item" to add new items.
                      </td>
                    </tr>
                  ) : (
                    complianceItems.map((item, idx) => (
                      <tr key={item.id} style={{ background: idx % 2 === 0 ? 'white' : '#f9fafb' }}>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>{idx + 1}</td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <input
                            type="text"
                            value={item.number || ''}
                            onChange={(e) => updateComplianceItem(idx, 'number', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <textarea
                            value={item.text || item.requirement || item.clause || ''}
                            onChange={(e) => updateComplianceItem(idx, 'text', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', minHeight: '60px', resize: 'vertical' }}
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <select
                            value={item.status || 'Open'}
                            onChange={(e) => updateComplianceItem(idx, 'status', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              background: item.status === 'Compliant' ? '#d1fae5' : 
                                          item.status === 'Deviation' ? '#fee2e2' : '#fef3c7',
                            }}
                          >
                            <option value="Open">Open</option>
                            <option value="Compliant">Compliant</option>
                            <option value="Deviation">Deviation</option>
                            <option value="Not Applicable">Not Applicable</option>
                          </select>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb' }}>
                          <textarea
                            value={item.response || ''}
                            onChange={(e) => updateComplianceItem(idx, 'response', e.target.value)}
                            style={{ width: '100%', padding: '6px', border: '1px solid #d1d5db', borderRadius: '4px', minHeight: '60px', resize: 'vertical' }}
                            placeholder="Enter response..."
                          />
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                          <button
                            onClick={() => removeComplianceItem(idx)}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                            }}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {/* Page 5: Assumptions and Clarifications */}
        {currentPage === 5 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#1e40af', borderBottom: '2px solid #2563eb', paddingBottom: '10px' }}>
                📝 Assumptions and Clarifications
              </h3>
              <button
                onClick={addAssumption}
                style={{
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                }}
              >
                + Add Assumption
              </button>
            </div>
            <div style={{ 
              background: '#f9fafb', 
              padding: '15px', 
              borderRadius: '8px', 
              marginBottom: '15px',
              border: '1px solid #e5e7eb'
            }}>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                ℹ️ Select the assumptions that apply to this proposal. You can edit, add new ones, or remove those that don't apply.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {assumptions.map((assumption, idx) => (
                <div
                  key={assumption.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '12px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={assumption.checked}
                    onChange={() => toggleAssumption(assumption.id)}
                    style={{
                      marginTop: '4px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <textarea
                      value={assumption.text}
                      onChange={(e) => updateAssumption(assumption.id, e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        minHeight: '60px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                      placeholder="Enter assumption text..."
                    />
                  </div>
                  <button
                    onClick={() => deleteAssumption(assumption.id)}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '14px',
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {assumptions.length === 0 && (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#6b7280',
                  background: 'white',
                  border: '2px dashed #e5e7eb',
                  borderRadius: '8px',
                }}>
                  No assumptions added. Click "Add Assumption" to add new assumptions.
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Page 6: Network Architecture */}
        {currentPage === 6 && (
          <div>
            <h3 style={{ marginTop: 0, color: '#1e40af', borderBottom: '2px solid #2563eb', paddingBottom: '10px' }}>
              🔌 Network Architecture
            </h3>
            {networkArchContent.architectureName || networkArchContent.systemDescription || networkArchContent.diagramImage ? (
              <div>
                {/* Architecture Name - Editable */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                    Architecture Name:
                  </label>
                  <input
                    type="text"
                    value={networkArchContent.architectureName}
                    onChange={(e) => setNetworkArchContent({ ...networkArchContent, architectureName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '16px',
                      fontWeight: '600',
                    }}
                    placeholder="Enter architecture name..."
                  />
                </div>
                
                {/* Export Status Display - Visible UI State */}
                {sldData && sldData.exportStatus && (
                  <div style={{
                    marginBottom: '15px',
                    padding: '12px 15px',
                    background: sldData.exportStatus === 'WITH_IMAGE' ? '#ecfdf5' : '#fef3c7',
                    border: `1px solid ${sldData.exportStatus === 'WITH_IMAGE' ? '#a7f3d0' : '#fcd34d'}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: sldData.exportStatus === 'WITH_IMAGE' ? '#059669' : '#d97706',
                  }}>
                    {sldData.exportStatus === 'WITH_IMAGE' ? (
                      <>✅ Diagram image available</>
                    ) : (
                      <>⚠️ Diagram image not available - saved with XML only {sldData.pngExportAttempts && `(${sldData.pngExportAttempts} attempts)`}</>
                    )}
                  </div>
                )}
                
                {/* Diagram Image */}
                {networkArchContent.diagramImage ? (
                  <div style={{ marginBottom: '25px' }}>
                    <h4 style={{ color: '#374151', marginBottom: '10px' }}>System Diagram:</h4>
                    <div style={{
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '15px',
                      background: 'white',
                      textAlign: 'center',
                    }}>
                      <img
                        src={networkArchContent.diagramImage}
                        alt="System Architecture Diagram"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        }}
                      />
                      {networkArchContent.architectureName && (
                        <p style={{
                          marginTop: '12px',
                          fontSize: '13px',
                          color: '#6b7280',
                          fontStyle: 'italic',
                        }}>
                          Figure: {networkArchContent.architectureName}
                        </p>
                      )}
                    </div>
                    <p style={{
                      marginTop: '10px',
                      fontSize: '12px',
                      color: '#6b7280',
                      fontStyle: 'italic',
                    }}>
                      💡 To update the diagram, go to System Architecture tab and click "Send to Proposal" again.
                    </p>
                  </div>
                ) : (
                  <div style={{
                    marginBottom: '25px',
                    padding: '20px',
                    background: '#f9fafb',
                    border: '2px dashed #d1d5db',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#6b7280',
                  }}>
                    <span style={{ fontSize: '48px', display: 'block', marginBottom: '10px' }}>📐</span>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                      Diagram image not available
                    </p>
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                      The diagram was saved as XML only. Go to System Architecture tab and click "Send to Proposal" to retry image export.
                    </p>
                  </div>
                )}
                
                {/* System Description - Editable */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                    System Description:
                  </label>
                  <textarea
                    value={networkArchContent.systemDescription}
                    onChange={(e) => setNetworkArchContent({ ...networkArchContent, systemDescription: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '15px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      lineHeight: '1.8',
                      minHeight: '200px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                    placeholder="Enter system description..."
                  />
                </div>
                
                {/* Last Updated Info */}
                {sldData && sldData.timestamp && (
                  <div style={{
                    marginTop: '15px',
                    padding: '10px',
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#059669',
                  }}>
                    ✅ Last received from System Architecture: {new Date(sldData.timestamp).toLocaleString()}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                padding: '60px 20px',
                textAlign: 'center',
                background: '#fef3c7',
                border: '2px dashed #f59e0b',
                borderRadius: '8px',
                color: '#92400e',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
                <h4 style={{ margin: '0 0 10px 0' }}>No Network Architecture Data Available</h4>
                <p style={{ margin: '0 0 15px 0', color: '#78350f' }}>
                  To add network architecture content to this proposal:
                </p>
                <ol style={{
                  textAlign: 'left',
                  display: 'inline-block',
                  margin: '0',
                  padding: '0 0 0 20px',
                  color: '#78350f',
                }}>
                  <li>Go to the <strong>System Architecture</strong> tab (SLD tab)</li>
                  <li>Create your network diagram using the diagrams.net editor</li>
                  <li>Click <strong>"🤖 Generate Description"</strong> to create AI description</li>
                  <li>Click <strong>"📤 Send to Proposal"</strong> button</li>
                  <li>Return here to edit the content before exporting</li>
                </ol>
              </div>
            )}
          </div>
        )}
        
        {/* Custom Sections */}
        {customSections.map(section => (
          section.pageNumber === currentPage && (
            <div key={section.id}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Section Title
                </label>
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateCustomSection(section.id, 'title', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '16px',
                    fontWeight: '600',
                  }}
                  placeholder="Enter section title..."
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151' }}>
                  Section Content
                </label>
                <div style={{ 
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                }}>
                  <ReactQuill
                    theme="snow"
                    value={section.content}
                    onChange={(value) => updateCustomSection(section.id, 'content', value)}
                    modules={quillModules}
                    formats={quillFormats}
                    style={{
                      minHeight: '400px',
                    }}
                    placeholder="Enter section content here. Use the toolbar to format text, add lists, tables, colors, and more..."
                  />
                </div>
              </div>
              <div style={{
                marginTop: '15px',
                padding: '12px',
                background: '#e0f2fe',
                border: '1px solid #7dd3fc',
                borderRadius: '6px',
                color: '#075985',
                fontSize: '13px',
                lineHeight: '1.6',
              }}>
                <strong>✨ Rich Text Features:</strong>
                <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
                  <li>Format text with <strong>bold</strong>, <em>italic</em>, <u>underline</u></li>
                  <li>Add headers, bullet points, and numbered lists</li>
                  <li>Change font colors and background colors</li>
                  <li>Insert links and images</li>
                  <li>Adjust text alignment and indentation</li>
                </ul>
              </div>
            </div>
          )
        ))}
      </div>
      
      {/* Page Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '20px',
        padding: '15px 20px',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            background: currentPage === 1 ? '#e5e7eb' : '#2563eb',
            color: currentPage === 1 ? '#9ca3af' : 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontWeight: '600',
          }}
        >
          ← Previous
        </button>
        <span style={{ color: '#6b7280' }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            background: currentPage === totalPages ? '#e5e7eb' : '#2563eb',
            color: currentPage === totalPages ? '#9ca3af' : 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontWeight: '600',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
